// The shot chart with everything the local app's chart had (firmware/frontend-orione/index.html drawCurve, Dominik
// 19.10.2026: "auch beim Graphen des Bezuges fehlen einige Parameter. In der orione.local hatten wir da viel mehr"):
// weight with a soft fill on the left scale, flow on its own scale, the brew temperature on the right, the time grid,
// where the pump stopped, the target weight, the pre-infusion (burst and pause) and another shot (the reference or a
// chosen one) faint and dashed behind it. Only scales, ticks and labels here; the component draws them.
import type { CurveData } from './curves.js';
import type { ScreenContext } from './context.js';
import { formatAuto, formatNumber } from './format.js';

export interface ShotChartView {
  /** seconds the time axis shows */
  seconds: number;
  /** grid lines with their labels ("10 s"); the start has none next to the weight scale's "0 g" */
  timeTicks: { s: number; label: string }[];
  /**
   * the weight scale from 0 (left): its top and three labels (0, half, and the target or else the most); `target`
   * marks the target's label, so no label has to sit inside the plot where the curve ends
   */
  weight: { max: number; ticks: { v: number; label: string; target?: boolean }[] } | null;
  /** the flow scale from 0 and its label ("2,3 g/s", above the plot at the top left) */
  flow: { max: number; label: string } | null;
  /** the temperature scale (right): at least 4 K */
  temp: { lo: number; hi: number; loLabel: string; hiLabel: string } | null;
  /** where the pump stopped (s), null if unknown */
  stopS: number | null;
  /** the target weight of a shot by weight: a line in the plot, its value on the scale, "Ziel 36 g" in the legend */
  target: { g: number; label: string } | null;
  /** the pre-infusion: burst, then the pause (s); a band, named in the legend */
  pi: { burst: number; pause: number } | null;
  /** a curve's entry has its unit in the label and the most of this shot (`max`, without the unit) */
  legend: {
    key: 'weight' | 'flow' | 'temp' | 'target' | 'stop' | 'pi' | 'other';
    label: string;
    max?: string;
  }[];
}

const values = (d: CurveData | null | undefined, key: 'weight' | 'flow' | 'temp'): number[] =>
  (d?.[key] ?? []).filter((v): v is number => v !== null && Number.isFinite(v));

const lengthS = (d: CurveData | null | undefined): number =>
  d ? (Math.max(d.weight?.length ?? 0, d.flow?.length ?? 0, d.temp?.length ?? 0) - 1) * (d.dt / 1000) : 0;

/**
 * The scales of one shot (d) and the shot behind it (other) on the same axes, as the local app drew them: weight
 * 0..max(10 g, target, the most)·1.1, flow 0..max(3 g/s, the most)·1.15, temperature on its own range of at least
 * 4 K; grid every 5, 10 or 20 s. A channel the shot does not have is absent (never drawn as zero).
 */
export function shotChart(
  ctx: ScreenContext,
  d: CurveData,
  other: CurveData | null,
  o: {
    targetWeight?: number | null;
    pi?: { burst: number; pause: number } | null;
    /** seconds the axis shows at least (the running shot grows into it) */
    spanS?: number;
    otherLabel?: string | null;
  } = {},
): ShotChartView {
  const all = other ? [d, other] : [d];
  const seconds = Math.max(o.spanS ?? 0, 1, ...all.map(lengthS));
  const n = (v: number, digits: number) => formatNumber(v, digits, ctx.lang);
  const step = seconds > 40 ? 20 : seconds > 16 ? 10 : 5;
  const timeTicks: ShotChartView['timeTicks'] = [];
  // "0 s" would touch "0 g" in the corner (review 19.10.2026); without a weight scale the corner is free
  for (let s = 0; s <= seconds + 0.01; s += step)
    timeTicks.push({ s, label: s === 0 && d.weight ? '' : `${s} s` });

  const weights = all.flatMap((x) => values(x, 'weight'));
  const target = o.targetWeight && o.targetWeight > 0 && d.weight ? o.targetWeight : null;
  const gTop = Math.max(10, target ?? 0, ...weights);
  // the target is the top label of the scale: the weight ends near it, a label in the plot sat on the curve
  const top = target ?? gTop;
  const weight = d.weight
    ? {
        max: gTop * 1.1,
        ticks: [
          { v: 0, label: '0 g' },
          { v: top / 2, label: `${Math.round(top / 2)} g` },
          target
            ? { v: target, label: `${formatAuto(target, ctx.lang)} g`, target: true }
            : { v: gTop, label: `${Math.round(gTop)} g` },
        ],
      }
    : null;

  const flows = all.flatMap((x) => values(x, 'flow'));
  const fTop = Math.max(3, ...flows);
  const flow = d.flow ? { max: fTop * 1.15, label: `${n(fTop, 1)} g/s` } : null;

  const temps = all.flatMap((x) => values(x, 'temp'));
  let temp: ShotChartView['temp'] = null;
  if (d.temp && temps.length) {
    let lo = Math.min(...temps);
    let hi = Math.max(...temps);
    if (hi - lo < 4) {
      const mid = (hi + lo) / 2;
      lo = mid - 2;
      hi = mid + 2;
    }
    temp = { lo, hi, loLabel: `${n(lo, 1)}°`, hiLabel: `${n(hi, 1)}°` };
  }

  const len = Math.max(d.weight?.length ?? 0, d.flow?.length ?? 0, d.temp?.length ?? 0);
  const stopS = d.stop >= 0 && d.stop < len ? (d.stop * d.dt) / 1000 : null;
  const pi = o.pi && o.pi.burst + o.pi.pause > 0 ? { burst: o.pi.burst, pause: o.pi.pause } : null;
  const targetLabel = target ? `${ctx.tr('goal')} ${formatAuto(target, ctx.lang)} g` : '';
  const most = (key: 'weight' | 'flow' | 'temp') => {
    const v = values(d, key);
    return v.length ? n(Math.max(...v), 1) : undefined;
  };
  const curve = (key: 'weight' | 'flow' | 'temp', label: string) => {
    const max = most(key);
    return max === undefined ? { key, label } : { key, label, max };
  };
  const legend: ShotChartView['legend'] = [
    ...(d.weight ? [curve('weight', ctx.tr('legendWeight'))] : []),
    ...(d.flow ? [curve('flow', ctx.tr('legendFlow'))] : []),
    ...(d.temp ? [curve('temp', ctx.tr('legendTemp'))] : []),
    ...(target ? [{ key: 'target' as const, label: targetLabel }] : []),
    ...(stopS !== null ? [{ key: 'stop' as const, label: ctx.tr('pumpOff') }] : []),
    ...(pi ? [{ key: 'pi' as const, label: ctx.tr('sPreinf') }] : []),
    ...(other && o.otherLabel ? [{ key: 'other' as const, label: o.otherLabel }] : []),
  ];
  return {
    seconds,
    timeTicks,
    weight,
    flow,
    temp,
    stopS,
    target: target ? { g: target, label: targetLabel } : null,
    pi,
    legend,
  };
}

/**
 * Seconds until 10 g are in the cup ("T10", Robert McKeon Aloe's dial-in measure, as the local app): from the weight
 * channel, between the two samples around 10 g; null without a scale or below 10 g.
 */
export function t10Of(d: CurveData | null | undefined): number | null {
  const w = d?.weight;
  if (!d || !w) return null;
  const k = w.findIndex((v) => v !== null && v >= 10);
  if (k < 0) return null;
  if (k === 0) return 0;
  const a = w[k - 1] ?? 0;
  const b = w[k] ?? 0;
  return b > a ? ((k - 1 + (10 - a) / (b - a)) * d.dt) / 1000 : (k * d.dt) / 1000;
}
